/**
 * User Notification Channels API Schemas
 *
 * Schemas for per-user, per-organization notification channel preferences.
 * Supports: in-app, email, telegram, webhook, bitrix24_chat channels.
 *
 * SINGLE SOURCE OF TRUTH for API contract.
 */

import { z } from 'zod/v4';
import { notificationCategorySchema } from './notifications';

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Update user notification channels request
 * All fields optional - only specified fields are updated
 */
export const updateUserNotificationChannelsRequestSchema = z
  .object({
    // In-app (always available, can't be fully disabled)
    inAppEnabled: z.boolean().optional(),

    // Email
    emailEnabled: z.boolean().optional(),
    customEmail: z.string().email().max(255).nullable().optional(),

    // Telegram
    telegramEnabled: z.boolean().optional(),
    telegramChatId: z.string().max(100).nullable().optional(),

    // Webhook
    webhookEnabled: z.boolean().optional(),
    webhookUrl: z.string().url().max(500).nullable().optional(),
    webhookSecret: z.string().max(255).nullable().optional(), // Only for setting, never returned

    // Bitrix24 Group Chat
    bitrix24ChatEnabled: z.boolean().optional(),
    bitrix24ChatId: z
      .string()
      .regex(/^\d+$/, 'Chat ID must be numeric')
      .max(20)
      .nullable()
      .optional(),

    // Category filter (null = all categories)
    categoryFilter: z.array(notificationCategorySchema).nullable().optional(),

    // Weekly report preference (null = inherit from org)
    weeklyReportEnabled: z.boolean().nullable().optional(),

    // Daily report preference (null = inherit from org)
    dailyReportEnabled: z.boolean().nullable().optional(),

    // Email of personal notices (null = inherit = on). The client sends it only
    // after the user moved the switch: this body is .strict(), so a client that
    // sent it always would break saving against an edge-api rolled back alone.
    personalEmailEnabled: z.boolean().nullable().optional(),
  })
  .strict();

/**
 * Test channel request
 *
 * Sends a test notification through specified channel.
 *
 * @property channel - Channel to test ('email', 'telegram', 'webhook', or 'bitrix24_chat')
 * @property target - Optional target override for testing (email address, chat_id, or webhook url)
 */
export const testNotificationChannelRequestSchema = z
  .object({
    /** Channel type to test - one of: email, telegram, webhook, bitrix24_chat */
    channel: z.enum(['email', 'telegram', 'webhook', 'bitrix24_chat']),
    /** Optional target override for testing (email address, chat_id, or webhook url) */
    target: z.string().max(500).optional(),
  })
  .strict();

/**
 * Confirm Telegram pairing code request
 *
 * Fallback path for a user who found the bot on their own (bare `/start`)
 * instead of using the one-shot deep link (Task 8): the bot hands out a
 * 6-digit code valid for 15 minutes, and the caller pastes it here to bind
 * the chat that requested it to their own account.
 *
 * @property code - The 6-digit pairing code shown by the bot
 */
export const confirmTelegramCodeRequestSchema = z
  .object({
    code: z.string().regex(/^\d{6}$/, 'Code must be 6 digits'),
  })
  .strict();

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * User notification channels response
 *
 * Contains all channel preferences for a user within an organization.
 * Note: webhookSecret is NEVER returned, only hasWebhookSecret flag.
 *
 * @property id - Unique identifier for this preferences record
 * @property userId - User ID these preferences belong to
 * @property orgId - Organization ID
 * @property inAppEnabled - In-app notifications enabled (always available, can't be fully disabled)
 * @property emailEnabled - Email notifications enabled
 * @property customEmail - Custom email override (null = use user's primary email)
 * @property telegramEnabled - Telegram notifications enabled
 * @property telegramChatId - Telegram chat ID for this user (null if not configured)
 * @property webhookEnabled - Webhook notifications enabled
 * @property webhookUrl - Webhook URL to POST notifications to (null if not configured)
 * @property hasWebhookSecret - True if webhook secret is configured (actual secret never returned)
 * @property categoryFilter - Array of notification categories to receive (null = all categories)
 * @property weeklyReportEnabled - Weekly report preference (null = inherit from org settings)
 * @property createdAt - ISO 8601 timestamp when preferences were created
 * @property updatedAt - ISO 8601 timestamp when preferences were last updated
 */
export const userNotificationChannelsResponseSchema = z.object({
  id: z.string(),
  userId: z.string(),
  orgId: z.string(),

  // In-app
  inAppEnabled: z.boolean(),

  // Email
  emailEnabled: z.boolean(),
  customEmail: z.string().nullable(),

  // Telegram
  telegramEnabled: z.boolean(),
  telegramChatId: z.string().nullable(),

  // Webhook
  webhookEnabled: z.boolean(),
  webhookUrl: z.string().nullable(),
  hasWebhookSecret: z.boolean(), // Flag only, never actual secret

  // Bitrix24 Group Chat
  bitrix24ChatEnabled: z.boolean(),
  bitrix24ChatId: z.string().nullable(),

  // Filters
  categoryFilter: z.array(notificationCategorySchema).nullable(),

  // Weekly report
  weeklyReportEnabled: z.boolean().nullable(),

  // Daily report
  dailyReportEnabled: z.boolean().nullable(),

  // Email of personal notices (null = inherit = on). Optional on the wire: the UI
  // validates this response, and an edge-api rolled back alone would omit the
  // field — that must not turn the settings screen into a load error.
  personalEmailEnabled: z.boolean().nullable().optional(),

  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * GET response - wrapped in data object
 *
 * @property allowedCategories - Categories this caller's role may ever see
 *   (Task 1's RBAC mapping, `getAllowedNotificationCategories`). The profile
 *   UI must only render toggles for these — a role never sees a category it
 *   cannot subscribe to.
 * @property inherited - Slice of `orgs.settings_json.notificationDefaults`
 *   for the caller's role. Only meaningful while `data.categoryFilter` is
 *   `null` (inheriting); once the user picks an explicit filter, these flags
 *   no longer apply. Keyed by category, present only for categories that
 *   have an org default set.
 */
export const getUserNotificationChannelsResponseSchema = z.object({
  data: userNotificationChannelsResponseSchema,
  allowedCategories: z.array(notificationCategorySchema),
  inherited: z.partialRecord(notificationCategorySchema, z.boolean()),
  /**
   * Effective digest state, already resolved through the same cascade the
   * digest sender uses (per-user override → org default → platform ON). The
   * raw per-user fields in `data` cannot answer "will I get one": `null` means
   * inherit, and a caller reading that as ON promised a digest to users whose
   * organization had turned digests off. Optional so an older client (or a
   * cached response) still validates.
   */
  digests: z.object({ daily: z.boolean(), weekly: z.boolean() }).optional(),
  /**
   * Can this user actually be mailed?
   *
   * `false` when the stored address is a `@no-mailbox.invalid` placeholder —
   * the Bitrix24 minimal scope carries no e-mail, so the row gets one by
   * construction. The delivery path already refuses those (`deliverableEmail`
   * in `EmailNotificationChannel.send`), but the hub had no way to know and
   * kept listing Email among the channels a person is "delivered through" —
   * the exact false promise that card exists to eliminate, on the one channel
   * where nothing visibly fails.
   *
   * Server-resolved because the client cannot tell: `GET /settings`, which
   * carries the profile address, requires `user.read` and 403s for the very
   * roles most likely to hold a placeholder. Optional so an older client or a
   * cached response still validates; absent is read as "deliverable", which
   * matches every account created outside the minimal scope.
   */
  emailDeliverable: z.boolean().optional(),
});

/**
 * PUT response - wrapped with success flag
 */
export const updateUserNotificationChannelsResponseSchema = z.object({
  success: z.boolean(),
  data: userNotificationChannelsResponseSchema,
});

/**
 * Test channel response
 *
 * Result of a test notification send attempt.
 *
 * @property success - True if test notification was sent successfully
 * @property channel - Channel that was tested ('email', 'telegram', 'webhook', or 'bitrix24_chat')
 * @property message - Success message (e.g., "Test email sent to user@example.com")
 * @property error - Error message if test failed (mutually exclusive with message)
 */
export const testNotificationChannelResponseSchema = z.object({
  success: z.boolean(),
  /** Channel that was tested - one of: email, telegram, webhook, bitrix24_chat */
  channel: z.enum(['email', 'telegram', 'webhook', 'bitrix24_chat']),
  message: z.string().optional(),
  error: z.string().optional(),
});

// ============================================================================
// Default values (for new users without explicit config)
// ============================================================================

/**
 * Default notification channel preferences for new users
 *
 * Applied when a user has no explicit channel configuration.
 * Provides sensible defaults: in-app and email enabled, other channels disabled.
 *
 * @property inAppEnabled - Always true (in-app is the core notification channel)
 * @property emailEnabled - True by default (most users want email notifications)
 * @property customEmail - Null (use user's primary email from profile)
 * @property telegramEnabled - False by default (requires explicit opt-in and setup)
 * @property telegramChatId - Null (user must configure Telegram)
 * @property webhookEnabled - False by default (requires explicit configuration)
 * @property webhookUrl - Null (user must provide webhook URL)
 * @property hasWebhookSecret - False (no secret configured by default)
 * @property categoryFilter - Null (receive all notification categories)
 * @property weeklyReportEnabled - Null (inherit organization's weekly report setting)
 */
export const defaultUserNotificationChannelsSchema = z.object({
  inAppEnabled: z.literal(true),
  // emailEnabled is opt-in (default false) — see migration 020. Was incorrectly
  // declared as literal(true) here; aligned with upsert default below.
  emailEnabled: z.literal(false),
  customEmail: z.null(),
  telegramEnabled: z.literal(false),
  telegramChatId: z.null(),
  webhookEnabled: z.literal(false),
  webhookUrl: z.null(),
  hasWebhookSecret: z.literal(false),
  bitrix24ChatEnabled: z.literal(false),
  bitrix24ChatId: z.null(),
  categoryFilter: z.null(), // All categories
  weeklyReportEnabled: z.null(), // Inherit from org
  dailyReportEnabled: z.null(), // Inherit from org
  personalEmailEnabled: z.null(), // Inherit = on
});

/**
 * Input schema for creating/upserting user notification channels (SSOT for defaults).
 * Used by the repository layer to apply defaults when fields are undefined.
 */
export const upsertUserNotificationChannelsInputSchema = z.object({
  inAppEnabled: z.boolean().default(true),
  emailEnabled: z.boolean().default(false),
  customEmail: z.string().email().max(255).nullable().default(null),
  telegramEnabled: z.boolean().default(false),
  telegramChatId: z.string().max(100).nullable().default(null),
  webhookEnabled: z.boolean().default(false),
  webhookUrl: z.string().url().max(500).nullable().default(null),
  hasWebhookSecret: z.boolean().default(false),
  bitrix24ChatEnabled: z.boolean().default(false),
  bitrix24ChatId: z.string().max(20).nullable().default(null),
  categoryFilter: z.array(notificationCategorySchema).nullable().default(null),
  weeklyReportEnabled: z.boolean().nullable().default(null),
  dailyReportEnabled: z.boolean().nullable().default(null),
  personalEmailEnabled: z.boolean().nullable().default(null),
});

// ============================================================================
// Type exports
// ============================================================================

export type UpdateUserNotificationChannelsRequest = z.infer<
  typeof updateUserNotificationChannelsRequestSchema
>;
export type TestNotificationChannelRequest = z.infer<
  typeof testNotificationChannelRequestSchema
>;
export type ConfirmTelegramCodeRequest = z.infer<typeof confirmTelegramCodeRequestSchema>;
export type UserNotificationChannelsResponse = z.infer<
  typeof userNotificationChannelsResponseSchema
>;
export type GetUserNotificationChannelsResponse = z.infer<
  typeof getUserNotificationChannelsResponseSchema
>;
export type UpdateUserNotificationChannelsResponse = z.infer<
  typeof updateUserNotificationChannelsResponseSchema
>;
export type TestNotificationChannelResponse = z.infer<
  typeof testNotificationChannelResponseSchema
>;
export type DefaultUserNotificationChannels = z.infer<
  typeof defaultUserNotificationChannelsSchema
>;
export type UpsertUserNotificationChannelsInput = z.infer<
  typeof upsertUserNotificationChannelsInputSchema
>;
